import { useEffect, useState } from "react";
import { Button } from "../ui/button";

const COOKIE_CONSENT_KEY = "magnify-cookie-consent";
const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_EVENT = "magnify-cookie-consent-change";

type CookieConsentStatus = "accepted" | "declined";

export function getSavedCookieConsent(): CookieConsentStatus | null {
  try {
    const savedConsent = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) return null;

    const consent = JSON.parse(savedConsent) as {
      status?: CookieConsentStatus;
      version?: number;
    };

    if (
      consent.version !== COOKIE_CONSENT_VERSION ||
      (consent.status !== "accepted" && consent.status !== "declined")
    ) {
      return null;
    }

    return consent.status;
  } catch {
    return null;
  }
}

function saveCookieConsent(status: CookieConsentStatus) {
  try {
    window.localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({
        status,
        version: COOKIE_CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      }),
    );
  } catch {
    // If storage is unavailable, still dismiss the banner for this page view.
  }

  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT));
}

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(getSavedCookieConsent() === null);
  }, []);

  if (!isVisible) return null;

  const handleChoice = (status: CookieConsentStatus) => {
    saveCookieConsent(status);
    setIsVisible(false);
  };

  return (
    <section
      aria-label="Cookie consent"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-3xl rounded-lg border border-primary-100/20 bg-slate-950/95 p-4 text-white shadow-2xl backdrop-blur md:bottom-6 md:flex md:items-center md:gap-5 md:p-5"
    >
      <div className="pr-9 md:flex-1 md:pr-0">
        <p className="mt-1 text-sm leading-6 text-slate-200">
          <strong>Cookie Preferences:</strong> We use cookies to understand
          site traffic and improve MagnifyBI. <br /> You can accept or decline
          non-essential cookies.
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:mt-0 md:flex md:flex-none">
        <Button
          type="button"
          variant="outlinePrimary"
          size="sm"
          onClick={() => handleChoice("declined")}
          className="border-primary-100/30 bg-transparent text-white hover:bg-white/10"
        >
          Decline
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => handleChoice("accepted")}
        >
          Accept
        </Button>
      </div>
    </section>
  );
};

export default CookieBanner;
